import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { ensureYoutubeApiReady } from '../../stores/playerListStore';
import { useMobxInstance } from '../../utils/mobxUtil';

class ViewStore {
    videoId: string | null = null;
    isInitialized = false;
    el: HTMLElement | null = null;
    player: YT.Player | null = null;
    /**
     * 플레이어 상태
     * -1은 실제 YT.PlayerState.UNSTARTED 값이지만
     * 스크립트가 로드되지 않았을 수 있으므로 직접 설정
     */
    playerState: YT.PlayerState = -1;

    constructor(videoId: string | null) {
        makeObservable(this, {
            player: observable,
            setPlayer: action,
            setPlayerState: action,
            clear: action,
        });

        this.videoId = videoId;
    }

    async initialize(el: HTMLElement): Promise<void> {
        if (typeof window === 'undefined' || !el) {
            return;
        }

        if (this.el === el && this.isInitialized) {
            return;
        }

        await ensureYoutubeApiReady();
        this.clear();

        const options: YT.PlayerOptions = {
            width: '100%',
            height: '100%',
            videoId: this.videoId || undefined,
            events: {
                onReady: () => {
                    this.setPlayer(instance);
                },
                onStateChange: (e: YT.OnStateChangeEvent) => {
                    runInAction(() => {
                        this.setPlayerState(e.data);
                    });
                },
                onError: (e: YT.OnErrorEvent) => {
                    if (import.meta.env.DEV) {
                        console.log('ERROR:', e);
                    }
                },
            },
        };

        const instance: YT.Player = new window.YT.Player(el, options);

        this.isInitialized = true;
    }

    get isPlaying(): boolean {
        return !!this.player && this.playerState === YT.PlayerState.PLAYING;
    }

    play(): void {
        this.player?.playVideo();
    }

    pause(): void {
        this.player?.pauseVideo();
    }

    setPlayer(player: YT.Player): void {
        this.player = player;
    }

    setPlayerState(playerState: YT.PlayerState): void {
        /*
            -1 –시작되지 않음
            0 – 종료
            1 – 재생 중
            2 – 일시중지
            3 – 버퍼링
            5 – 동영상 신호
        */
        this.playerState = playerState;
    }

    clear(): void {
        this.player?.destroy();
        this.player = null;
        this.el = null;
    }
}

const YoutubePlayer = observer(({ videoId }: { videoId: string }): React.ReactNode => {
    const elRef = useRef<HTMLDivElement>(null);
    const [isMaskClicked, setMaskClicked] = useState(false);
    const viewStore = useMobxInstance(ViewStore, videoId);

    useEffect(() => {
        const el = elRef.current;

        if (viewStore && el) {
            viewStore.initialize(el);
        }

        return () => {
            if (viewStore) {
                viewStore.clear();
            }
        };
    }, [viewStore]);

    // iOS 에서는 터치 스타트 때 유튜브가 바로 재생되어 버리는 이슈가 있어, 명시적으로 클릭했을 때만 재생하도록 한다.
    // 한 번 재생한 후에 사용자가 트랙을 이동하는 등의 별도의 컨트롤을 할 수 있으므로 다시 마스크를 올리진 않는다.
    let mask: ReactNode;
    if (!isMaskClicked && !viewStore.isPlaying) {
        mask = (
            <div
                role="button"
                className="absolute top-0 left-0 h-full w-full cursor-pointer"
                onClick={(e) => {
                    e.preventDefault();
                    setMaskClicked(true);
                    viewStore.play();
                }}
            ></div>
        );
    }

    return (
        <div className="relative leading-none">
            {/* 임베딩한 유튜브 사이즈 최적화
            @see https://community.canvaslms.com/t5/Canvas-Instructional-Designer/Embedding-a-YouTube-Player-with-Responsive-Aspect-Ratio/ba-p/273447 */}
            <div className="relative h-0 overflow-hidden pb-[56.25%]">
                <div ref={elRef} className="absolute top-0 left-0 h-full w-full"></div>
                {mask}
            </div>
        </div>
    );
});

export default YoutubePlayer;
